export type RangeFilterValues = [number, number];
export type RangeFilterDraft = [string, string];

export interface RangeFilterLimits {
  min: number;
  max: number;
}

function isSafeRangeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER;
}

export function getRangeFilterLimits(min: unknown, max: unknown): RangeFilterLimits | null {
  return isSafeRangeNumber(min) && isSafeRangeNumber(max) && min <= max ? { min, max } : null;
}

export function canUseRangeSlider(limits: RangeFilterLimits | null, step: number): boolean {
  if (!limits || !isSafeRangeNumber(step) || step <= 0 || limits.min === limits.max) return false;

  const steps = (limits.max - limits.min) / step;

  return Number.isSafeInteger(Math.round(steps)) && steps >= 1 && Math.abs(steps - Math.round(steps)) < 1e-7;
}

/** Parse the whole input: never turn pasted text such as "12kg" into a valid number. */
function parseRangeBoundary(text: string, fallback: number): number | null {
  if (text.length > 64) return null;

  const normalized = text.trim().replace(',', '.');

  if (!normalized) return fallback;
  if (!/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null;

  const value = Number(normalized);

  return isSafeRangeNumber(value) ? value : null;
}

export function validateRangeFilterDraft(
  draft: RangeFilterDraft,
  limits: RangeFilterLimits | null,
): { values: RangeFilterValues | null; errors: RangeFilterDraft; queryValue?: string } {
  if (!limits) {
    return { values: null, errors: ['Диапазон недоступен', 'Диапазон недоступен'] };
  }

  const min = parseRangeBoundary(draft[0], limits.min);
  const max = parseRangeBoundary(draft[1], limits.max);
  const errors: RangeFilterDraft = [min, max].map((value) => {
    if (value === null) return 'Введите число';
    if (value < limits.min || value > limits.max) return `От ${limits.min} до ${limits.max}`;

    return '';
  }) as RangeFilterDraft;

  if (min === null || max === null || errors.some(Boolean)) return { values: null, errors };

  if (min > max) {
    return { values: null, errors: ['«От» должно быть не больше «До»', '«До» должно быть не меньше «От»'] };
  }

  return {
    values: [min, max],
    errors,
    queryValue: draft.every((value) => !value.trim()) ? undefined : `${min};${max}`,
  };
}

/** Server values are also untrusted; an invalid snapshot must not reach the slider. */
export function getSafeRangeFilterValues(values: unknown, limits: RangeFilterLimits): RangeFilterValues {
  if (
    Array.isArray(values) &&
    values.length === 2 &&
    values.every(isSafeRangeNumber) &&
    values[0] >= limits.min &&
    values[1] <= limits.max &&
    values[0] <= values[1]
  ) {
    return [values[0], values[1]];
  }

  return [limits.min, limits.max];
}

export type RangeFilterQuery = Record<string, string | null | (string | null)[] | undefined>;

export interface RangeFilterDefinition {
  slug: string;
  type: string;
  details?: { limit_min: number; limit_max: number };
}

export function sanitizeRangeFilterQuery(query: RangeFilterQuery, filters: RangeFilterDefinition[] = []) {
  const cleaned = { ...query };
  const definitions = new Map<string, RangeFilterLimits | null>([['price', { min: 0, max: Number.MAX_SAFE_INTEGER }]]);
  const hasPriceLimits = filters.some((filter) => filter.slug === 'price' && filter.type === 'range');

  for (const filter of filters) {
    if (filter.type === 'range') {
      definitions.set(filter.slug, getRangeFilterLimits(filter.details?.limit_min, filter.details?.limit_max));
    }
  }

  let changed = false;

  for (const [slug, limits] of definitions) {
    const key = `filter[${slug}]`;
    if (!(key in query)) continue;

    const value = query[key];
    const parts = typeof value === 'string' ? value.split(';') : [];
    let normalized = parts.length === 2 ? validateRangeFilterDraft([parts[0], parts[1]], limits).queryValue : undefined;

    // An open price boundary needs the actual catalog limit, not the broad syntax-check fallback.
    if (slug === 'price' && !hasPriceLimits && normalized !== undefined && parts.some((part) => !part.trim())) {
      normalized = parts.map((part) => part.trim().replace(',', '.')).join(';');
    }

    if (normalized === undefined) {
      delete cleaned[key];
      changed = true;
    } else if (normalized !== value) {
      cleaned[key] = normalized;
      changed = true;
    }
  }

  return { query: cleaned, changed };
}

/** Filter types are not encoded in their slugs. Discover them without submitting untrusted selections. */
export function getRangeFilterMetadataQuery(query: RangeFilterQuery): RangeFilterQuery {
  return Object.fromEntries(
    Object.entries(query).filter(([key]) => key !== 'page' && (!key.startsWith('filter[') || key === 'filter[search]')),
  );
}

export function getRangeFilterMetadataKey(path: string, query: RangeFilterQuery): string {
  return JSON.stringify([path, query['filter[search]'] ?? null]);
}

export function getRangeFilterDefinitions(filters: RangeFilterDefinition[]): RangeFilterDefinition[] {
  return filters
    .filter((filter) => filter.type === 'range')
    .map(({ slug, type, details }) => ({ slug, type, details }));
}

export function getRangeFilterEndpoint(path: string): string | null {
  if (path.startsWith('/catalog/')) return `/catalog/show/${path.slice('/catalog/'.length)}`;
  if (path.startsWith('/brands/')) return `/catalog/brands/${path.slice('/brands/'.length)}`;
  if (path === '/sale' || path.startsWith('/sale/')) return `/catalog/sales${path.slice('/sale'.length)}`;
  if (path === '/new') return '/catalog/new';
  if (path === '/search') return '/search';

  return null;
}

export interface RangeFilterMetadata {
  key: string;
  filters: RangeFilterDefinition[];
}
